import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { IMG } from "@/lib/images";
import PageBanner from "@/components/common/PageBanner";
import SmartImage from "@/components/common/SmartImage";

export default function CaseStudies() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/public/case-studies").then((r) => setItems(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="cases-page" className="bg-[var(--bone)]">
      <PageBanner
        eyebrow="Selected Work"
        title="Campaigns we&apos;re proud of —"
        italicWord="numbers, not narratives."
        kicker="Brand × City editorial showcases of campaigns shipped on Fundle."
        image={IMG.highStreet}
      />
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-20 grid grid-cols-1 md:grid-cols-2 gap-10">
        {items.map((c, idx) => (
          <motion.div
            key={c.id}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: idx * 0.07 }}
          >
            <Link to={`/case-studies/${c.id}`} className="block group" data-testid={`case-${idx}`}>
              <div className="relative aspect-[16/10] overflow-hidden">
                <SmartImage src={c.cover_image} fallback={IMG.aiPlannerMag} alt={c.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 img-luxe" />
                <div className="absolute inset-0 hero-overlay" />
                <div className="absolute top-5 left-5 chip bg-white/95">{c.industry}</div>
              </div>
              <div className="mt-6 flex items-start justify-between gap-6">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] font-semibold text-[var(--gold)]">
                    {c.brand} <span className="opacity-50">|</span> {c.cities[0]}
                  </div>
                  <div className="mt-2 font-serif-display text-2xl md:text-3xl text-[var(--ink)] leading-tight">{c.title}</div>
                </div>
                <ArrowUpRight size={22} className="text-[var(--ink)] shrink-0 mt-2 group-hover:text-[var(--gold)]" />
              </div>
              <div className="mt-5 grid grid-cols-3 gap-4">
                <Pill label="Reach" value={`${c.reach_millions}M`} />
                <Pill label="Impressions" value={`${c.impressions_millions}M`} />
                <Pill label="Engagement" value={`${c.engagement_rate}%`} />
              </div>
            </Link>
          </motion.div>
        ))}
      </section>
    </div>
  );
}

function Pill({ label, value }) {
  return (
    <div className="border-t border-[var(--border)] pt-3">
      <div className="font-serif text-xl text-[var(--ink)]">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--slate-2)]">{label}</div>
    </div>
  );
}
